/**
 * L'UN landing engine.
 *
 * - window.LunScroll: scroll watching with spring momentum, independent of which element scrolls
 *   (Helio scrolls `.page-wrapper` on desktop >= 990px and the document on mobile, so scroll events are
 *   captured on window and every position is read from getBoundingClientRect).
 * - [data-lun-words]  words fade in with scroll (the engine splits the text, inline markup is kept).
 * - [data-lun-draw]   an SVG <path> draws itself with its section's scroll progress.
 * - [data-lun-reveal] a quiet fade/rise when the element enters.
 * - The promise thread: one pink line that weaves down the landing page, with momentum. Each section owns its
 *   stretch: it names its gutter with data-lun-thread-side (left|right, -narrow for phones), its rhythm comes
 *   from its own key, and stretches meet at ports smoothed separately, so editing one section leaves the
 *   thread in the others alone. Check changes with .image-gen/thread-snapshot.js (record + diff).
 * - Drag-to-scroll rows and a "Choose size" button that reuses the theme's quick-add modal.
 * Everything degrades to fully visible content without JS or with reduced motion.
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const SVG_NS = 'http://www.w3.org/2000/svg';

const clamp = (v, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
const lerp = (a, b, t) => a + (b - a) * t;

/* ------------------------------------------------------------------ spring */

/**
 * A slightly under-damped spring: follows a target with a little lag and a soft landing.
 * Stiffness 120 / damping ratio 0.92 reads as "weighted" without feeling floaty.
 */
class Spring {
    constructor(value = 0, stiffness = 120, ratio = 0.92) {
        this.x = value;
        this.v = 0;
        this.k = stiffness;
        this.c = 2 * Math.sqrt(stiffness) * ratio;
    }

    /** @param {number} target @param {number} dt seconds */
    step(target, dt) {
        const steps = dt > 1 / 50 ? 2 : 1;
        const h = dt / steps;
        for (let i = 0; i < steps; i += 1) {
            const a = this.k * (target - this.x) - this.c * this.v;
            this.v += a * h;
            this.x += this.v * h;
        }
        return this.x;
    }

    /** @param {number} target */
    settled(target) {
        return Math.abs(target - this.x) < 0.0006 && Math.abs(this.v) < 0.0006;
    }

    /** @param {number} value */
    snap(value) {
        this.x = value;
        this.v = 0;
    }
}

/* ------------------------------------------------------------ scroll engine */

/** @type {Set<{ el: Element, cb: Function, sp: Spring, near: boolean, raw: number }>} */
const watchers = new Set();
/** @type {Array<() => boolean>} frame callbacks: return true while they still need frames */
const frameTasks = [];
let rafId = 0;
let lastTime = 0;
let dirty = true;

function viewportHeight() {
    return window.innerHeight || document.documentElement.clientHeight;
}

let stableWidth = 0;
let stableHeight = 0;

/**
 * The tallest viewport seen at this width. Phone browsers change innerHeight as their toolbars slide away;
 * the thread works from this stable value so it never jumps when that happens.
 */
function stableViewportHeight() {
    const width = window.innerWidth;
    if (width !== stableWidth) {
        stableWidth = width;
        stableHeight = 0;
    }
    stableHeight = Math.max(stableHeight, viewportHeight());
    return stableHeight;
}

/**
 * 0 when the element's top meets the viewport bottom, 1 when its bottom meets the viewport top.
 * @param {DOMRect} rect
 * @param {number} vh
 */
function rawProgress(rect, vh) {
    return clamp((vh - rect.top) / (vh + rect.height));
}

const reported = new Set();

/** One throwing callback must not take every other animation down with it; say so once and carry on. */
function reportOnce(kind, error) {
    if (reported.has(kind)) return;
    reported.add(kind);
    console.warn(`[lun] ${kind} failed and was skipped`, error);
}

function frame(now) {
    rafId = 0;
    const dt = clamp((now - lastTime) / 1000, 0.001, 1 / 20);
    lastTime = now;
    const vh = viewportHeight();
    let busy = dirty;
    dirty = false;

    for (const w of watchers) {
        if (!w.el.isConnected) {
            watchers.delete(w);
            continue;
        }
        const rect = w.el.getBoundingClientRect();
        const margin = vh * 0.6;
        const near = rect.bottom > -margin && rect.top < vh + margin;
        if (!near && !w.near) continue;
        const raw = rawProgress(rect, vh);
        const p = reduceMotion.matches ? raw : w.sp.step(raw, dt);
        const v = w.sp.v;
        w.raw = raw;
        try {
            w.cb({raw, p, v});
        } catch (error) {
            reportOnce('watcher', error);
        }
        const settled = reduceMotion.matches || w.sp.settled(raw);
        w.near = near || !settled;
        if (!settled) busy = true;
    }

    for (let i = frameTasks.length - 1; i >= 0; i -= 1) {
        try {
            if (frameTasks[i](dt, vh)) busy = true;
        } catch (error) {
            reportOnce('task', error);
        }
    }

    if (busy) rafId = requestAnimationFrame(frame);
}

function wake() {
    dirty = true;
    if (!rafId) {
        lastTime = performance.now();
        rafId = requestAnimationFrame(frame);
    }
}

// Capture-phase scroll listener on window sees the scroll of any element, including `.page-wrapper`.
window.addEventListener(
    'scroll',
    (event) => {
        const t = event.target;
        if (t === document || t === document.documentElement || (t instanceof Element && t.classList.contains('page-wrapper'))) {
            wake();
        }
    },
    {passive: true, capture: true}
);
window.addEventListener('resize', wake, {passive: true});

/**
 * @param {Element} el
 * @param {(state: { raw: number, p: number, v: number }) => void} cb
 * @returns {() => void} unwatch
 */
function watch(el, cb) {
    const w = {el, cb, sp: new Spring(0), near: true, raw: 0};
    const rect = el.getBoundingClientRect();
    const raw = rawProgress(rect, viewportHeight());
    w.sp.snap(raw);
    watchers.add(w);
    wake();
    return () => watchers.delete(w);
}

window.LunScroll = {
    watch,
    get reduced() {
        return reduceMotion.matches;
    },
};

/* ----------------------------------------------------------------- reveals */

/** @type {IntersectionObserver | null} */
let revealObserver = null;

/** @param {ParentNode} root */
function initReveals(root = document) {
    const targets = root.querySelectorAll('[data-lun-reveal]:not([data-lun-armed])');
    if (!targets.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
        for (const el of targets) el.classList.add('is-in');
        return;
    }

    revealObserver ??= new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                entry.target.classList.add('is-in');
                revealObserver?.unobserve(entry.target);
                window.setTimeout(queueThread, 1100);
            }
        },
        {rootMargin: '0px 0px -8% 0px', threshold: 0.08}
    );

    for (const el of targets) {
        el.setAttribute('data-lun-armed', '');
        revealObserver.observe(el);
    }
}

/* ------------------------------------------------------------------- words */

/** @type {Set<{ el: HTMLElement, words: HTMLElement[], sp: Spring }>} */
const wordGroups = new Set();
let wordTaskAdded = false;

/** @param {HTMLElement} root */
function splitWords(root) {
    const words = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    for (const node of nodes) {
        const text = node.textContent ?? '';
        if (!text.trim()) continue;
        const fragment = document.createDocumentFragment();
        for (const part of text.split(/(\s+)/)) {
            if (!part) continue;
            if (/^\s+$/.test(part)) {
                fragment.append(document.createTextNode(part));
                continue;
            }
            const span = document.createElement('span');
            span.className = 'lun-word';
            span.style.display = 'inline-block';
            span.textContent = part;
            words.push(span);
            fragment.append(span);
        }
        node.replaceWith(fragment);
    }
    return words;
}

const WORD_FLOOR = 0.12;

function wordTask(dt, vh) {
    let busy = false;
    for (const g of wordGroups) {
        if (!g.el.isConnected) {
            wordGroups.delete(g);
            continue;
        }
        const rect = g.el.getBoundingClientRect();
        if (rect.bottom < -vh * 0.5 || rect.top > vh * 1.5) continue;
        // start while the block is still low on screen, finish once it sits in the upper half
        const target = clamp((vh * 0.92 - rect.top) / (vh * 0.42 + rect.height * 0.9));
        const p = reduceMotion.matches ? 1 : g.sp.step(target, dt);
        const n = g.words.length;
        for (let i = 0; i < n; i += 1) {
            const t = clamp((p * (n + 4) - i) / 4);
            g.words[i].style.opacity = String(lerp(WORD_FLOOR, 1, t * t * (3 - 2 * t)).toFixed(3));
        }
        g.el.style.setProperty('--lun-p', p.toFixed(4));
        if (!reduceMotion.matches && !g.sp.settled(target)) busy = true;
    }
    return busy;
}

/** @param {ParentNode} root */
function initWords(root = document) {
    for (const el of root.querySelectorAll('[data-lun-words]:not([data-lun-split])')) {
        if (!(el instanceof HTMLElement)) continue;
        el.setAttribute('data-lun-split', '');
        if (reduceMotion.matches) continue;
        const words = splitWords(el);
        if (!words.length) continue;
        for (const w of words) w.style.opacity = String(WORD_FLOOR);
        const rect = el.getBoundingClientRect();
        const sp = new Spring(0);
        sp.snap(clamp((viewportHeight() * 0.92 - rect.top) / (viewportHeight() * 0.42 + rect.height * 0.9)));
        wordGroups.add({el, words, sp});
    }
    if (wordGroups.size && !wordTaskAdded) {
        wordTaskAdded = true;
        frameTasks.push(wordTask);
    }
    wake();
}

/* ------------------------------------------------------------- local draws */

/** @type {Set<{ path: SVGPathElement, host: Element, sp: Spring, start: number, end: number }>} */
const draws = new Set();
let drawTaskAdded = false;

function drawTask(dt, vh) {
    let busy = false;
    for (const d of draws) {
        if (!d.path.isConnected) {
            draws.delete(d);
            continue;
        }
        const rect = d.host.getBoundingClientRect();
        if (rect.bottom < -vh * 0.5 || rect.top > vh * 1.5) continue;
        const raw = rawProgress(rect, vh);
        const p = reduceMotion.matches ? raw : d.sp.step(raw, dt);
        const t = clamp((p - d.start) / Math.max(0.0001, d.end - d.start));
        d.path.style.strokeDashoffset = String((1 - t).toFixed(4));
        if (!reduceMotion.matches && !d.sp.settled(raw)) busy = true;
    }
    return busy;
}

/** @param {ParentNode} root */
function initDraws(root = document) {
    for (const el of root.querySelectorAll('[data-lun-draw]:not([data-lun-draw-ready])')) {
        if (!(el instanceof SVGPathElement)) continue;
        el.setAttribute('data-lun-draw-ready', '');
        el.setAttribute('pathLength', '1');
        el.style.strokeDasharray = '1';
        if (reduceMotion.matches) {
            el.style.strokeDashoffset = '0';
            continue;
        }
        el.style.strokeDashoffset = '1';
        const host = el.closest('.shopify-section') ?? el.closest('section') ?? el.parentElement;
        if (!host) continue;
        const start = Number.parseFloat(el.getAttribute('data-lun-draw-start') ?? '0.15');
        const end = Number.parseFloat(el.getAttribute('data-lun-draw-end') ?? '0.75');
        const sp = new Spring(0, 90);
        sp.snap(rawProgress(host.getBoundingClientRect(), viewportHeight()));
        draws.add({path: el, host, sp, start, end});
    }
    if (draws.size && !drawTaskAdded) {
        drawTaskAdded = true;
        frameTasks.push(drawTask);
    }
    wake();
}

/* -------------------------------------------------------- the promise thread */

/**
 * One pink line that weaves down the landing page and draws itself with scroll.
 * Geometry comes from the sections themselves (no markers):
 *  - the thread runs in the reserved side channel (--lun-thread-gutter) and starts on the LEFT;
 *  - at every section boundary it crosses to the other side in a wide S, using the clear padding;
 *  - crossings next to a `data-lun-thread-mode="flat"` section get a loop;
 *  - a `data-lun-thread-mode="end"` section gets the knot (two loops and a tail) at its bottom.
 * Speed: every stretch of path is given a scroll budget of max(vertical travel, 0.5 x its length), so a
 * swoop that spans little height still takes real scrolling and never "zaps" across the screen; the tip
 * then follows that map through a spring, so it lags the scroll and eases to rest.
 */

/** @type {{ host: HTMLElement, svg: SVGSVGElement, lagZones: {top:number,bottom:number}[], signature?: string, paths: SVGPathElement[], segLens: number[], starts: number[], shown: number, dot: SVGCircleElement, total: number, budget: Float32Array, lens: Float32Array, y0: number, y1: number, sp: Spring } | null} */
let thread = null;
let threadQueued = false;
let threadTaskAdded = false;
const SAMPLES = 900;

function threadGutter(W) {
    if (W >= 1700) return 150;
    if (W >= 1100) return 120;
    if (W >= 750) return 72;
    return 32;
}

/**
 * Catmull-Rom through the points, with the ends steered by optional neighbours outside the run.
 * @param {{x:number,y:number}[]} pts @param {{x:number,y:number}|null} before @param {{x:number,y:number}|null} after
 */
function smoothRun(pts, before, after) {
    if (pts.length < 2) return '';
    let d = '';
    for (let i = 0; i < pts.length - 1; i += 1) {
        const p0 = pts[i - 1] ?? before ?? pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] ?? after ?? p2;
        // Centripetal parameterisation (alpha 0.5): tangents scale with the spacing of the points, so a short step
        // after a long one can't overshoot into a cusp or a little loop the way uniform Catmull-Rom does.
        const d1 = Math.max(Math.sqrt(Math.hypot(p1.x - p0.x, p1.y - p0.y)), 1e-3);
        const d2 = Math.max(Math.sqrt(Math.hypot(p2.x - p1.x, p2.y - p1.y)), 1e-3);
        const d3 = Math.max(Math.sqrt(Math.hypot(p3.x - p2.x, p3.y - p2.y)), 1e-3);
        const a1 = p0 === p1 ? null : 3 * d1 * (d1 + d2);
        const a2 = p3 === p2 ? null : 3 * d3 * (d3 + d2);
        const c1x = a1 ? (d1 * d1 * p2.x - d2 * d2 * p0.x + (2 * d1 * d1 + 3 * d1 * d2 + d2 * d2) * p1.x) / a1 : p1.x + (p2.x - p1.x) / 3;
        const c1y = a1 ? (d1 * d1 * p2.y - d2 * d2 * p0.y + (2 * d1 * d1 + 3 * d1 * d2 + d2 * d2) * p1.y) / a1 : p1.y + (p2.y - p1.y) / 3;
        const c2x = a2 ? (d3 * d3 * p1.x - d2 * d2 * p3.x + (2 * d3 * d3 + 3 * d3 * d2 + d2 * d2) * p2.x) / a2 : p2.x - (p2.x - p1.x) / 3;
        const c2y = a2 ? (d3 * d3 * p1.y - d2 * d2 * p3.y + (2 * d3 * d3 + 3 * d3 * d2 + d2 * d2) * p2.y) / a2 : p2.y - (p2.y - p1.y) / 3;
        d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    return d;
}

/**
 * A stretch split at its ports (indices of points shared by two sections): one continuous path, each run
 * smoothed with its neighbours across the ports.
 * @param {{x:number,y:number}[]} pts @param {number[]} ports
 */
function portedPath(pts, ports) {
    if (pts.length < 2) return '';
    const cuts = [0, ...ports.filter((i) => i > 0 && i < pts.length - 1), pts.length - 1];
    let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
    for (let k = 0; k < cuts.length - 1; k += 1) {
        const run = pts.slice(cuts[k], cuts[k + 1] + 1);
        const a = run[0];
        const b = run[run.length - 1];
        // Each end is steered by its real neighbour across the port, so the join is as smooth as anywhere else
        // (a forced vertical tangent kinked where a crossing arrives sideways). Only the one curve on either side
        // of a port sees the other section's nearest point.
        const before = k > 0 ? pts[cuts[k] - 1] : null;
        const after = k < cuts.length - 2 ? pts[cuts[k + 1] + 1] : null;
        d += smoothRun(run, before, after);
    }
    return d;
}

/** Stable small hash of a section key, so each section's rhythm depends on nothing but itself. */
function keyHash(key) {
    let h = 7;
    for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return h;
}

/**
 * Points of a loop (one full turn) centred on (cx, cy), entering heading down.
 * @param {number} cx @param {number} cy @param {number} r @param {1|-1} dir
 */
function loopPoints(cx, cy, r, dir) {
    const pts = [];
    const n = 16;
    for (let i = 0; i <= n; i += 1) {
        const a = Math.PI / 2 + dir * (i / n) * Math.PI * 2; // start at the bottom of the circle, heading sideways
        // A hand-drawn wobble that fades out at the base, so the loop leaves exactly where it came in.
        const wobble = 1 + 0.08 * Math.sin(i * 1.7) * Math.sin((Math.PI * i) / n);
        pts.push({x: cx + Math.cos(a) * r * wobble, y: cy + Math.sin(a) * r});
    }
    return pts;
}

function teardownThread() {
    thread?.svg.remove();
    thread = null;
}

const CONTENT_SELECTOR = 'h1,h2,h3,h4,p,a,button,img,picture,figure,li,[data-lun-content]';

/**
 * Vertical extent of the real content (text, media, controls) inside a section, relative to the host.
 * The thread only crosses where this says the page is empty.
 * @param {Element} el @param {number} hostTop
 * @returns {{ top: number, bottom: number } | null}
 */
function contentBounds(el, hostTop) {
    let top = Infinity;
    let bottom = -Infinity;
    for (const node of el.querySelectorAll(CONTENT_SELECTOR)) {
        if (node.closest('svg, [inert], [data-lun-thread-ignore]')) continue;
        if (node.tagName === 'LI' && !node.textContent?.trim() && !node.querySelector('img')) continue;
        const r = node.getBoundingClientRect();
        if (r.width < 8 || r.height < 8) continue;
        top = Math.min(top, r.top - hostTop);
        bottom = Math.max(bottom, r.bottom - hostTop);
    }
    return Number.isFinite(top) ? {top, bottom} : null;
}

function buildThread() {
    threadQueued = false;
    const host = document.getElementById('MainContent');
    const hero = document.querySelector('lun-hero-wrist-component');
    if (!host || !hero) {
        teardownThread();
        return;
    }

    const sections = [...host.children].filter(
        (el) => el instanceof HTMLElement && el.classList.contains('shopify-section') && el.offsetHeight > 40
    );
    const heroSection = hero.closest('.shopify-section');
    const startIndex = sections.indexOf(heroSection) + 2; // first section after the hero and the marquee
    const list = sections.slice(Math.max(1, startIndex));
    if (list.length < 2) {
        teardownThread();
        return;
    }

    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';

    const hostRect = host.getBoundingClientRect();
    const W = Math.round(hostRect.width);
    const H = Math.round(hostRect.height);
    const vh = stableViewportHeight();

    // Skip the (expensive) rebuild when nothing it depends on has moved.
    const anchors = [...host.querySelectorAll('[data-lun-thread-entry],[data-lun-thread-exit],[data-lun-thread-exit-narrow]')].map((el) => {
        const r = el.getBoundingClientRect();
        return `${Math.round((r.left - hostRect.left) / 4)},${Math.round((r.top - hostRect.top) / 4)}`;
    });
    const signature = [W, H, ...list.map((el) => {
        const r = el.getBoundingClientRect();
        return `${Math.round(r.top - hostRect.top)}:${Math.round(r.height)}`;
    }), ...anchors].join('|');
    if (thread && thread.host === host && thread.svg.isConnected && thread.total && thread.signature === signature) return;

    const gutter = threadGutter(W);
    document.documentElement.style.setProperty('--lun-thread-gutter', `${gutter}px`);
    const leftX = gutter * 0.5;
    const rightX = W - gutter * 0.5;

    if (!thread || thread.host !== host || !thread.svg.isConnected) {
        teardownThread();
        const svg = document.createElementNS(SVG_NS, 'svg');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        svg.setAttribute('class', 'lun-thread');
        svg.style.cssText = 'position:absolute;inset-block-start:0;inset-inline-start:0;pointer-events:none;z-index:3;overflow:visible;';
        const path = document.createElementNS(SVG_NS, 'path');
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke-width', W < 750 ? '2' : '2.25');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        path.style.stroke = 'var(--lun-pink, #F4869F)';
        const dot = document.createElementNS(SVG_NS, 'circle');
        dot.setAttribute('r', W < 750 ? '3.5' : '4.5');
        dot.style.fill = 'var(--lun-pink, #F4869F)';
        svg.append(path, dot);
        host.append(svg);
        thread = {
            host,
            svg,
            paths: [path],
            lagZones: [],
            dot,
            total: 0,
            segLens: [],
            starts: [],
            shown: 0,
            scrollAt: new Float32Array(0),
            lens: new Float32Array(0),
            y0: 0,
            y1: 1,
            sp: new Spring(0, 34, 1)
        };
    }

    const state = thread;
    state.svg.setAttribute('width', String(W));
    state.svg.setAttribute('height', String(H));
    state.svg.setAttribute('viewBox', `0 0 ${W} ${H}`);

    // Section geometry relative to the host.
    const narrowView = W < 750;
    const geo = list.map((el) => {
        const r = el.getBoundingClientRect();
        const cs = getComputedStyle(el.firstElementChild ?? el);
        // Which gutter the thread runs down in this section: declared by the section itself
        // (data-lun-thread-side, with a -narrow override for phones), so it never depends on the sections above.
        const sideEl = el.hasAttribute('data-lun-thread-side') ? el : el.querySelector('[data-lun-thread-side]');
        const sideAttr = (narrowView && sideEl?.getAttribute('data-lun-thread-side-narrow')) || sideEl?.getAttribute('data-lun-thread-side');
        const key = el.id.replace(/^shopify-section-(template--\d+__)?/, '') || String(el.dataset.sectionType ?? '');
        return {
            el,
            key,
            hash: keyHash(key),
            want: sideAttr === 'left' ? 'L' : sideAttr === 'right' ? 'R' : null,
            top: r.top - hostRect.top,
            bottom: r.bottom - hostRect.top,
            flat: (el.querySelector('[data-lun-thread-mode]') ?? el).getAttribute('data-lun-thread-mode') === 'flat' || el.querySelector('[data-lun-thread-mode="flat"]') !== null,
            end: el.querySelector('[data-lun-thread-mode="end"]') !== null,
            lag: el.querySelector('[data-lun-thread-lag]') !== null,
            padTop: Number.parseFloat(cs.paddingTop) || 0,
            padBottom: Number.parseFloat(cs.paddingBottom) || 0,
            content: contentBounds(el, hostRect.top),
        };
    });

    // Sections where the tip may trail the reader (it catches up again before the next one).
    state.lagZones = [];
    for (const g of geo) {
        if (!g.lag) continue;
        const prevZone = state.lagZones[state.lagZones.length - 1];
        if (prevZone && Math.abs(prevZone.bottom - g.top) < 4) prevZone.bottom = g.bottom;
        else state.lagZones.push({top: g.top, bottom: g.bottom});
    }

    // Bridges: a section can mark an entry and an exit (data-lun-thread-entry / -exit, value = top|bottom|left|right).
    // The thread flows into the entry, the section's own threads carry on, and the thread leaves from the exit.
    const anchorPoint = (el, where, fallback) => {
        const r = el.getBoundingClientRect();
        const w = where || fallback;
        const cx = r.left - hostRect.left + r.width / 2;
        const cy = r.top - hostRect.top + r.height / 2;
        if (w === 'right') return {x: r.right - hostRect.left, y: cy, where: w};
        if (w === 'left') return {x: r.left - hostRect.left, y: cy, where: w};
        if (w === 'bottom') return {x: cx, y: r.bottom - hostRect.top, where: w};
        return {x: cx, y: r.top - hostRect.top, where: 'top'};
    };
    const narrow = W < 990; // phones and tablets: bridge exits settle into a gutter through a clear band
    const bridges = new Map();
    geo.forEach((g, index) => {
        const narrowEntry = narrow ? g.el.querySelector('[data-lun-thread-entry-narrow]') : null;
        const entryEl = narrowEntry ?? g.el.querySelector('[data-lun-thread-entry]');
        // The thread always leaves from the exit anchor (e.g. the far end of a timeline line), on phones too.
        // On phones `data-lun-thread-exit-narrow` (left|right) names the gutter it then settles into, so it stays
        // out of a rail that bleeds off the other edge. Without the attribute a side exit settles on the left.
        // A section with only the narrow attribute keeps the older meaning: that element is the exit anchor.
        const exitEl = g.el.querySelector('[data-lun-thread-exit]');
        const narrowExit = narrow ? g.el.querySelector('[data-lun-thread-exit-narrow]') : null;
        const anchorEl = exitEl ?? narrowExit;
        if (!entryEl || !anchorEl) return;
        const exit = anchorPoint(
            anchorEl,
            exitEl ? exitEl.getAttribute('data-lun-thread-exit') : narrowExit?.getAttribute('data-lun-thread-exit-narrow'),
            'bottom'
        );
        let settle = null;
        if (narrow && (exit.where === 'left' || exit.where === 'right')) {
            settle = narrowExit?.getAttribute('data-lun-thread-exit-narrow') === 'right' ? 'R' : 'L';
        }
        bridges.set(index, {
            entry: anchorPoint(entryEl, narrowEntry ? narrowEntry.getAttribute('data-lun-thread-entry-narrow') : entryEl.getAttribute('data-lun-thread-entry'), 'top'),
            exit,
            settle,
        });
    });

    /**
     * First clear horizontal band below `fromY` in a section, between xMin and xMax: the middle of the first
     * gap of at least `minGap` px between content boxes (host coordinates). Used to cross the page under a
     * bridge exit without touching text or cards.
     * @param {Element} el @param {number} fromY @param {number} xMin @param {number} xMax @param {number} minGap
     */
    const clearBandBelow = (el, fromY, xMin, xMax, minGap) => {
        const boxes = [];
        for (const node of el.querySelectorAll(CONTENT_SELECTOR)) {
            if (node.closest('svg, [inert], [data-lun-thread-ignore]')) continue;
            if (node.tagName === 'LI' && !node.textContent?.trim() && !node.querySelector('img')) continue;
            const r = node.getBoundingClientRect();
            if (r.width < 8 || r.height < 8) continue;
            const top = r.top - hostRect.top;
            const bottom = r.bottom - hostRect.top;
            const left = r.left - hostRect.left;
            const right = r.right - hostRect.left;
            if (bottom <= fromY || right < xMin || left > xMax) continue;
            boxes.push({top, bottom});
        }
        boxes.sort((a, b) => a.top - b.top);
        let cursor = fromY;
        for (const box of boxes) {
            if (box.top - cursor >= minGap) return cursor + (box.top - cursor) / 2;
            cursor = Math.max(cursor, box.bottom);
        }
        return cursor + minGap;
    };
    /** @type {{x:number,y:number}[][]} */
    const segments = [];
    /** @type {number[][]} port indices per segment (points shared by two sections) */
    const segmentPorts = [];
    /** @type {number[]} */
    let ports = [];
    const markPort = () => ports.push(pts.length - 1);

    const pts = [];
    const step = clamp(vh * 0.5, 280, 460);
    let side = 'L';
    let sideX = leftX;
    // Index where the latest section's meander began: beats that reach into a crossing or a bridge approach
    // are dropped, so those shapes depend on the sections' content and not on where the meander ended.
    let meanderFrom = -1;
    pts.push({x: sideX, y: geo[0].top + Math.min(60, geo[0].padTop * 0.5)});

    for (let i = 0; i < geo.length; i += 1) {
        const g = geo[i];
        const next = geo[i + 1];
        const wob = gutter * 0.28;

        const bridge = bridges.get(i);
        if (bridge) {
            const {entry, exit} = bridge;
            // Finish the stretch by sweeping into the entry ...
            // pass above this section's first content, never through it
            const clearY = Math.min(entry.y - 150, (g.content ? g.content.top : entry.y) - 34);
            meanderFrom = -1;
            const last = pts[pts.length - 1];
            // ... and only once the section above has run out of content, so the sweep never cuts through it.
            const prev = geo[i - 1];
            const prevClear = prev?.content ? Math.min(prev.content.bottom + 28, g.top + 20) : -Infinity;
            if (prev?.content && last.y < prevClear - 60) pts.push({x: sideX, y: prevClear - 40});
            // Sweep across in the middle of the empty gap between the two sections' content when there is one.
            const gapMid = prev?.content && g.content && g.content.top - prev.content.bottom > 90 ? (prev.content.bottom + g.content.top) / 2 : null;
            const approachY = Math.max(pts[pts.length - 1].y + 40, gapMid ?? Math.max(clearY, g.top + 20));
            const from = pts[pts.length - 1];
            // Quarter turns are drawn as arcs (0deg / 45deg / 90deg points), never as hooks that overshoot and turn back.
            if (entry.where === 'left') {
                // come down the margin, then a quarter turn into the left edge of the entry (clear of text beside it)
                const R = clamp(entry.x - leftX, 12, 48);
                if (from.y < entry.y - R - 90) pts.push({x: leftX, y: entry.y - R - 60});
                pts.push({x: leftX, y: entry.y - R});
                pts.push({x: leftX + R * 0.29, y: entry.y - R * 0.29});
                pts.push({x: leftX + R, y: entry.y});
                if (entry.x > leftX + R + 2) pts.push({x: entry.x, y: entry.y});
            } else {
                // turn out of the gutter, run along the clear gap, and turn down onto the entry
                const d = Math.sign(entry.x - from.x) || 1;
                const span = Math.abs(entry.x - from.x);
                let r1 = clamp(span * 0.2, 24, 80);
                let r2 = clamp(span * 0.2, 12, 80);
                if (r1 + r2 > span - 8) {
                    r1 = Math.max(6, (span - 8) * 0.5);
                    r2 = r1;
                }
                r1 = Math.max(6, Math.min(r1, approachY - from.y - 12));
                r2 = Math.max(6, Math.min(r2, entry.y - approachY - 6));
                pts.push({x: from.x, y: approachY - r1});
                pts.push({x: from.x + d * r1 * 0.29, y: approachY - r1 * 0.29});
                pts.push({x: from.x + d * r1, y: approachY});
                pts.push({x: entry.x - d * r2, y: approachY});
                pts.push({x: entry.x - d * r2 * 0.29, y: approachY + r2 * 0.29});
                pts.push({x: entry.x, y: approachY + r2});
                pts.push({x: entry.x, y: entry.y + 2});
            }
            segments.push(pts.splice(0));
            segmentPorts.push(ports);
            ports = [];
            // ... and start the next one at the exit, drifting to the nearest gutter (on phones: the settle gutter).
            // The gutter to settle in: on phones the exit says (-narrow); otherwise the next section's declared side,
            // so a bridge never hands the following section the wrong gutter.
            const nextWant = geo[i + 1]?.want ?? null;
            const settle = bridge.settle ?? ((exit.where === 'right' || exit.where === 'left') ? nextWant : null);
            const toRight = settle ? settle === 'R' : exit.x > W / 2;
            side = toRight ? 'R' : 'L';
            sideX = toRight ? rightX : leftX;
            const out = exit.where === 'right' ? 1 : -1;
            if (settle && (exit.where === 'right' || exit.where === 'left') && (toRight ? out < 0 : out > 0)) {
                // Phones: the thread carries on from the end of the line, turns down beside it, then crosses back under
                // the section's content (through the first clear band, e.g. between a hint and a card rail) to its gutter.
                const turnX = clamp(exit.x + out * 30, leftX, rightX);
                const crossY = clearBandBelow(g.el, exit.y + 12, Math.min(turnX, sideX), Math.max(turnX, sideX), 30);
                pts.push({x: exit.x - out * 2, y: exit.y});
                pts.push({x: exit.x + out * 18, y: exit.y + 3});
                pts.push({x: turnX, y: exit.y + 30});
                if (crossY - 34 > exit.y + 64) pts.push({x: turnX, y: crossY - 34});
                pts.push({x: turnX - out * 26, y: crossY - 4});
                pts.push({x: turnX + (sideX - turnX) * 0.5, y: crossY});
                pts.push({x: sideX + out * 24, y: crossY + 6});
                pts.push({x: sideX, y: crossY + 56});
                continue;
            }
            if (exit.where === 'right') {
                pts.push({x: exit.x - 2, y: exit.y});
                pts.push({x: exit.x + 36, y: exit.y + 6});
                pts.push({x: exit.x + (sideX - exit.x) * 0.7, y: exit.y + 80});
            } else if (exit.where === 'left') {
                pts.push({x: exit.x + 2, y: exit.y});
                pts.push({x: exit.x - 18, y: exit.y + 6});
                pts.push({x: exit.x + (sideX - exit.x) * 0.7, y: exit.y + 80});
            } else {
                pts.push({x: exit.x, y: exit.y - 2});
                pts.push({x: exit.x, y: exit.y + 34});
                pts.push({x: exit.x + (sideX - exit.x) * 0.55, y: exit.y + 110});
            }
            pts.push({x: sideX, y: exit.y + 190});
            continue;
        }

        // Meander down this section inside the channel.
        const yEnd = (next ? g.bottom - Math.min(g.padBottom, 260) * 0.9 : g.bottom - 120);
        // The rhythm comes from the section's own key and a local counter, never from what came before.
        let y = pts[pts.length - 1].y + step * 0.6;
        let flip = g.hash % 2 ? 1 : -1;
        let beat = 0;
        meanderFrom = pts.length;
        while (y < yEnd - 60) {
            pts.push({x: sideX + flip * wob, y});
            flip *= -1;
            beat += 1;
            y += step * (0.85 + 0.3 * ((g.hash + beat) % 3) * 0.5);
        }

        if (g.end) {
            // The ending: one medium heart, drawn in a single stroke that closes on itself.
            const narrow = W < 750;
            // On phones the heart sits just under the button, tucked inside the margin; on wide screens it hangs in the gutter.
            const ky = narrow ? Math.max(pts[pts.length - 1].y + 100, g.bottom - 112) : Math.max(pts[pts.length - 1].y + 140, g.bottom - Math.min(520, (g.bottom - g.top) * 0.4));
            const k = narrow ? 2.1 : 2.7;
            const dir = side === 'L' ? 1 : -1;
            const cx = sideX + dir * k * 16 * (narrow ? 1.5 : 0.55);
            pts.push({x: sideX, y: ky - 90});
            pts.push({x: sideX + (cx - sideX) * 0.6, y: ky - 40});
            const beats = 44;
            for (let b = 0; b <= beats; b += 1) {
                const t = (b / beats) * (Math.PI * 2 + 0.32); // a little past the start so the stroke crosses itself
                const f = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
                pts.push({x: cx + 16 * Math.sin(t) ** 3 * k * -dir, y: ky + (5 - f) * k});
            }
            break;
        }

        if (!next) break;

        if (bridges.has(i + 1)) continue; // the entry sweep above handles the approach

        // Cross only where the page is genuinely empty: below this section's last content and above the next one's first.
        const gBottom = g.content ? g.content.bottom : g.bottom - g.padBottom;
        const nTop = next.content ? next.content.top : next.top + next.padTop;
        // The band is set by the two sections' content alone (not by where this section's meander happened to
        // end), so the crossing between them looks the same whatever comes above; meander beats that reach into
        // the band are dropped instead of pushing it down.
        while (meanderFrom >= 0 && pts.length > meanderFrom && pts[pts.length - 1].y > gBottom - 12) pts.pop();
        // Fallback for a stretch that already ends lower (e.g. a bridge exit): start the band below it.
        const bandTop = Math.max(gBottom + 28, pts[pts.length - 1].y + 30);
        const bandBottom = nTop - 28;
        // The next section names its side; without a declaration the thread alternates where it can.
        const nextSide = next.want ?? (side === 'L' ? 'R' : 'L');
        const roomToCross =
            bandBottom - bandTop >= (next.want ? 110 : 150) &&
            (next.want || ((g.bottom - g.top) >= 650 && (next.bottom - next.top) >= 650));
        if (nextSide === side || !roomToCross) {
            // Stay in the gutter (no clear band, or the next section wants this side). The point is a port, so the
            // next section starts from here whatever this one drew.
            pts.push({x: sideX, y: Math.min(next.bottom, Math.max(pts[pts.length - 1].y + 40, next.top + 40))});
            markPort();
            continue;
        }

        // Cross to the other side in a wide S inside that empty band.
        const nextX = nextSide === 'L' ? leftX : rightX;
        const crossTop = bandTop;
        const crossBottom = bandBottom;
        const dy = crossBottom - crossTop;
        const dx = nextX - sideX;
        const sweep = Math.min(W * 0.1, 120) * (g.hash % 2 ? 1 : -1);
        const needsLoop = g.flat || next.flat;

        pts.push({x: sideX + dx * 0.04, y: crossTop});
        pts.push({x: sideX + dx * 0.28 + sweep, y: crossTop + dy * 0.28});
        if (needsLoop) {
            const r = clamp(dy * 0.16, 26, W < 750 ? 38 : 62);
            const mx = sideX + dx * 0.52;
            const my = crossTop + dy * 0.5;
            const dir = dx > 0 ? -1 : 1;
            const t = -dir; // direction of travel
            // (only where they keep moving forward, on narrow screens the sweep points may already be that close)
            const leadIn = {x: mx - t * r * 1.5, y: my + r};
            if ((leadIn.x - pts[pts.length - 1].x) * t > 8) pts.push(leadIn);
            pts.push(...loopPoints(mx, my, r, dir));
            const leadOut = {x: mx + t * r * 1.5, y: my + r};
            if ((sideX + dx * 0.8 - sweep * 0.7 - leadOut.x) * t > 8) pts.push(leadOut);
        } else {
            pts.push({x: sideX + dx * 0.55, y: crossTop + dy * 0.5});
        }
        pts.push({x: sideX + dx * 0.8 - sweep * 0.7, y: crossTop + dy * 0.76});
        pts.push({x: nextX, y: crossBottom});
        markPort();
        side = nextSide;
        sideX = nextX;
    }

    segments.push(pts);
    segmentPorts.push(ports);
    const drawable = segments.filter((seg) => seg.length > 1);
    const drawablePorts = segmentPorts.filter((_, k) => segments[k].length > 1);
    // One <path> per stretch; the stretches in between are drawn by the sections' own threads.
    while (state.paths.length < drawable.length) {
        const extra = state.paths[0].cloneNode(false);
        state.svg.insertBefore(extra, state.dot);
        state.paths.push(extra);
    }
    while (state.paths.length > drawable.length) state.paths.pop()?.remove();
    const segLens = [];
    drawable.forEach((seg, k) => {
        state.paths[k].setAttribute('d', portedPath(seg, drawablePorts[k]));
        segLens.push(state.paths[k].getTotalLength());
    });
    const starts = [];
    let total = 0;
    for (const l of segLens) {
        starts.push(total);
        total += l;
    }
    state.total = total;
    state.segLens = segLens;
    state.starts = starts;
    /** @param {number} len */
    const pointAt = (len) => {
        let k = segLens.length - 1;
        while (k > 0 && len < starts[k]) k -= 1;
        return state.paths[k].getPointAtLength(clamp(len - starts[k], 0, segLens[k]));
    };

    // Scroll position (in host y) at which the tip reaches each sample. Loops and sideways sweeps cost extra
    // scroll, so they unspool slowly; but that lag is bounded (and repaid on straight stretches), so outside
    // the lag zones the tip never trails the reader by more than a third of the screen.
    const scrollAt = new Float32Array(SAMPLES + 1);
    const lens = new Float32Array(SAMPLES + 1);
    const baseLag = vh * 0.3;
    const zoneLag = vh * 1.4;
    let prev = pointAt(0);
    let maxY = prev.y;
    scrollAt[0] = prev.y;
    for (let n = 1; n <= SAMPLES; n += 1) {
        const len = (total * n) / SAMPLES;
        const p = pointAt(len);
        const forward = Math.max(0, p.y - maxY);
        maxY = Math.max(maxY, p.y);
        const lagBefore = Math.max(0, scrollAt[n - 1] - (maxY - forward));
        let step = forward;
        if (!starts.some((st, k) => k > 0 && lens[n - 1] < st && len >= st)) {
            const ds = Math.hypot(p.x - prev.x, p.y - prev.y);
            const ratio = ds > 0 ? Math.min(1, forward / ds) : 1;
            const surcharge = ds * 1.5 * (1 - ratio); // extra scroll for loops and sideways travel
            step = forward + surcharge - Math.min(lagBefore, forward * 0.5); // repay some lag while descending
        }
        const inZone = state.lagZones.some((z) => p.y > z.top && p.y < z.bottom);
        // Just after a lag zone the thread leads the reader a little, so it has caught up before the next section.
        const afterZone = !inZone && state.lagZones.some((z) => p.y >= z.bottom && p.y < z.bottom + vh * 0.6);
        const allowance = inZone ? zoneLag : afterZone ? -vh * 0.18 : baseLag;
        scrollAt[n] = Math.min(scrollAt[n - 1] + Math.max(0.01, step), maxY + allowance);
        lens[n] = len;
        prev = p;
    }
    // Enforce monotone order from the end: where the lag allowance drops (leaving a lag zone) the earlier
    // samples are pulled forward, which is the tip catching up just before the next section arrives.
    for (let n = SAMPLES - 1; n >= 0; n -= 1) scrollAt[n] = Math.min(scrollAt[n], scrollAt[n + 1] - 0.01);
    state.scrollAt = scrollAt;
    state.lens = lens;
    state.y0 = drawable[0][0].y;
    state.y1 = maxY;
    state.shown = Math.min(state.shown, 1);
    state.signature = signature;

    state.paths.forEach((path, k) => {
        path.style.strokeDasharray = `${segLens[k]} ${segLens[k]}`;
    });
    if (!threadTaskAdded) {
        threadTaskAdded = true;
        frameTasks.push(threadTask);
    }
    wake();
}

/** Length of path drawn for a reading position, through the scroll-budget map. */
function threadTarget(state) {
    const hostRect = state.host.getBoundingClientRect();
    const reading = stableViewportHeight() * 0.66 - hostRect.top; // y inside the host that the reader is "at"
    const at = state.scrollAt;
    if (reading <= at[0]) return 0;
    if (reading >= at[SAMPLES]) return 1;
    let lo = 0;
    let hi = SAMPLES;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (at[mid] <= reading) lo = mid;
        else hi = mid;
    }
    const t = clamp((reading - at[lo]) / (at[hi] - at[lo] || 1));
    return lerp(state.lens[lo], state.lens[hi], t) / state.total;
}

/** Fastest the thread tip may travel, in path pixels per second. */
const MAX_TIP_SPEED = 1000;

function threadTask(dt) {
    const state = thread;
    if (!state || !state.total) return false;

    if (reduceMotion.matches || document.documentElement.dataset.lunThreadDebug === 'full') {
        for (const path of state.paths) path.style.strokeDashoffset = '0';
        state.dot.style.opacity = '0';
        return false;
    }

    const target = threadTarget(state);
    const raw = state.sp.step(target, dt);
    // Never let the tip travel faster than a calm pace, however hard the page is flicked.
    // Keep the tip in view: past an allowed lag it speeds up to catch the reader, except inside the
    // lag zones, where it may trail and then catches up before the next section arrives.
    const vh = stableViewportHeight();
    const readY = vh * 0.66 - state.host.getBoundingClientRect().top;
    const inLagZone = state.lagZones.some((z) => readY > z.top && readY < z.bottom - vh * 0.3);
    const lagPx = Math.max(0, (target - state.shown) * state.total);
    const boost = inLagZone ? 0 : Math.max(0, lagPx - vh * 0.3) * 14;
    const retreat = target < state.shown ? 3.5 : 1; // winding back up the page is allowed to be quicker
    const maxStep = ((MAX_TIP_SPEED * retreat + boost) * Math.min(dt, 0.05)) / state.total;
    state.shown += clamp(raw - state.shown, -maxStep, maxStep);
    const pos = state.shown;
    const drawn = clamp(pos) * state.total;

    let tip = 0;
    state.paths.forEach((path, k) => {
        const covered = clamp(drawn - state.starts[k], 0, state.segLens[k]);
        path.style.strokeDashoffset = String((state.segLens[k] - covered).toFixed(2));
        if (drawn >= state.starts[k]) tip = k;
    });

    const local = drawn - state.starts[tip];
    const atBridge = tip < state.paths.length - 1 && local >= state.segLens[tip] - 1;
    if (pos > 0.0015 && pos < 0.998 && !atBridge) {
        const pt = state.paths[tip].getPointAtLength(clamp(local, 0, state.segLens[tip]));
        state.dot.setAttribute('cx', pt.x.toFixed(1));
        state.dot.setAttribute('cy', pt.y.toFixed(1));
        state.dot.style.opacity = '1';
    } else {
        state.dot.style.opacity = '0';
    }

    return !(state.sp.settled(target) && Math.abs(state.shown - raw) < 0.0004);
}

function queueThread() {
    if (threadQueued) return;
    threadQueued = true;
    window.setTimeout(() => requestAnimationFrame(buildThread), 120);
}

/** @type {ResizeObserver | null} */
let threadObserver = null;

function initThread() {
    if (!document.querySelector('lun-hero-wrist-component')) return;
    queueThread();
    if (threadObserver || !('ResizeObserver' in window)) return;
    const host = document.getElementById('MainContent');
    if (!host) return;
    threadObserver = new ResizeObserver(queueThread);
    threadObserver.observe(host);
    document.fonts?.ready.then(queueThread);
}

// Phone browsers fire resize while their toolbars slide away mid-scroll; only a real width change
// (or a big height change) is worth rebuilding the path for, otherwise the thread would jump.
let lastViewport = {w: window.innerWidth, h: window.innerHeight};
window.addEventListener(
    'resize',
    () => {
        const w = window.innerWidth;
        const h = window.innerHeight;
        if (Math.abs(w - lastViewport.w) < 2 && Math.abs(h - lastViewport.h) < 150) return;
        lastViewport = {w, h};
        queueThread();
    },
    {passive: true}
);
window.addEventListener('load', queueThread);
const onReduceMotionChange = () => {
    queueThread();
    wake();
};
if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', onReduceMotionChange);
else reduceMotion.addListener?.(onReduceMotionChange);

/* ------------------------------------------------------------ drag to scroll */

/**
 * Drives the .lun-rail-progress hairline that follows a scroll row: the fill grows from the share of the row
 * that is visible to the whole row, the same maths as the product plates. The hairline is hidden when the
 * row has nothing to scroll.
 * @param {HTMLElement} rail
 */
function initRailProgress(rail) {
    const bar = rail.nextElementSibling;
    if (!(bar instanceof HTMLElement) || !bar.classList.contains('lun-rail-progress')) return;

    const update = () => {
        const max = rail.scrollWidth - rail.clientWidth;
        bar.hidden = max <= 1;
        const share = rail.scrollWidth ? rail.clientWidth / rail.scrollWidth : 1;
        const progress = max > 0 ? Math.min(1, Math.abs(rail.scrollLeft) / max) : 0;
        bar.style.setProperty('--lun-rail-p', String(share + (1 - share) * progress));
    };

    rail.addEventListener('scroll', update, {passive: true});
    window.addEventListener('resize', update, {passive: true});
    // images and fonts change the row width after the first paint
    if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(update);
        observer.observe(rail);
        for (const child of rail.children) observer.observe(child);
    }
    update();
}

/** @param {ParentNode} root */
function initDrag(root = document) {
    for (const el of root.querySelectorAll('[data-lun-drag]:not([data-lun-drag-ready])')) {
        if (!(el instanceof HTMLElement)) continue;
        el.setAttribute('data-lun-drag-ready', '');
        initRailProgress(el);

        let startX = 0;
        let startLeft = 0;
        let dragging = false;
        let moved = false;
        let activeId = -1;

        el.addEventListener('pointerdown', (event) => {
            if (event.pointerType !== 'mouse' || event.button !== 0) return;
            if (el.scrollWidth <= el.clientWidth) return;
            activeId = event.pointerId;
            startX = event.clientX;
            startLeft = el.scrollLeft;
            dragging = true;
            moved = false;
        });

        el.addEventListener('pointermove', (event) => {
            if (!dragging || event.pointerId !== activeId) return;
            const dx = event.clientX - startX;
            if (!moved && Math.abs(dx) < 6) return;
            if (!moved) {
                moved = true;
                el.classList.add('is-dragging');
                el.setPointerCapture(activeId);
            }
            el.scrollLeft = startLeft - dx;
        });

        const end = (event) => {
            if (!dragging || event.pointerId !== activeId) return;
            dragging = false;
            el.classList.remove('is-dragging');
            if (el.hasPointerCapture(activeId)) el.releasePointerCapture(activeId);
            // the click that follows a drag is swallowed above; if none arrives, do not leave the flag set
            window.setTimeout(() => {
                moved = false;
            }, 60);
        };

        el.addEventListener('pointerup', end);
        el.addEventListener('pointercancel', end);

        // A drag that ends on a link must not follow it.
        el.addEventListener(
            'click',
            (event) => {
                if (!moved) return;
                moved = false;
                event.preventDefault();
                event.stopPropagation();
            },
            true
        );
    }
}

/* ------------------------------------------------------------ quick add */

let quickAddStarted = false;

async function initQuickAdd() {
    if (quickAddStarted || !document.querySelector('lun-quick-add-component') || customElements.get('lun-quick-add-component')) return;
    quickAddStarted = true;

    try {
        const {QuickAddComponent} = await import('@theme/quick-add');

        class LunQuickAdd extends QuickAddComponent {
            get productPageUrl() {
                return this.dataset.productUrl || '';
            }

            connectedCallback() {
                super.connectedCallback();
                this.addEventListener('click', this.#onClick);
            }

            disconnectedCallback() {
                super.disconnectedCallback();
                this.removeEventListener('click', this.#onClick);
            }

            /** @param {MouseEvent} event */
            #onClick = (event) => {
                const trigger = event.target instanceof Element ? event.target.closest('[data-lun-quick-add]') : null;
                if (!trigger) return;
                // Let modified clicks open the product page normally.
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
                this.handleClick(event);
            };
        }

        customElements.define('lun-quick-add-component', LunQuickAdd);
    } catch (error) {
        // Without the module the trigger remains a plain link to the product page.
        console.warn('[lun] quick add unavailable', error);
    }
}

/* ----------------------------------------------------------------- boot */

function init(root = document) {
    initReveals(root);
    initWords(root);
    initDraws(root);
    initDrag(root);
    initThread();
}

init();
initQuickAdd();
window.dispatchEvent(new Event('lun:scroll-ready'));

document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) {
        init(event.target.ownerDocument);
        initQuickAdd();
        queueThread();
    }
});
